import { memo, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Play, Pause, Volume2, VolumeX, Plus, Minus, ThumbsUp, ThumbsDown,
  User, Trophy, Settings, Info, Music, Heart, SkipForward, Gamepad2
} from 'lucide-react'
import {
  collection, query, where, getDocs, doc, setDoc, onSnapshot,
  addDoc, increment, serverTimestamp, orderBy, limit
} from 'firebase/firestore'
import { db } from '../firebase/config'
import { toast } from '../hooks/useToast'
import DedicaceSpotlight from '../components/Dedicaces/DedicaceSpotlight'
import { useCurrentDedicaces } from '../hooks/useCurrentDedicaces'
import './RadioPageTV.css'

/* =====================================================================
   ACCUEIL TV — tout sur un seul écran, pilotable à la télécommande.

   Fluidité :
   - Chaque zone qui change souvent (heure, dédicaces, titre suivant,
     votes) est un petit composant isolé : seul lui se redessine.
   - Plus de vinyle qui tourne, plus de flou, plus de bandeau de
     20 dédicaces collées : rien qui oblige la box à tout repeindre.
   - La navigation aux flèches est gérée pour toute l'app par
     hooks/useTVRemote.js (calcul seulement à l'appui d'une touche).
   ===================================================================== */

const RADIO_API = 'https://api.radioking.io/widget/radio/radio-johnny-hallyday'

const MENU = [
  { path: '/profil', icon: User, label: 'Mon profil' },
  { path: '/classement', icon: Trophy, label: 'Classement' },
  { path: '/parametres', icon: Settings, label: 'Paramètres' },
  { path: '/a-propos', icon: Info, label: 'À propos' }
]

const songIdOf = (title) =>
  title ? title.toLowerCase().replace(/[^a-z0-9]/g, '_') : null

// ---------------------------------------------------------------------
// Heure (se redessine toutes les 20 s, et seule)
// ---------------------------------------------------------------------
const Clock = memo(function Clock() {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 20000)
    return () => clearInterval(iv)
  }, [])
  return (
    <span className="tvh-clock">
      {String(now.getHours()).padStart(2, '0')}:{String(now.getMinutes()).padStart(2, '0')}
    </span>
  )
})

// ---------------------------------------------------------------------
// Dédicaces (une à la fois)
// ---------------------------------------------------------------------
const Dedicaces = memo(function Dedicaces() {
  // Dédicaces en cours uniquement (approuvées, non archivées)
  const items = useCurrentDedicaces(20)

  return (
    <section className="tvh-dedic" aria-label="Dédicaces">
      <span className="tvh-dedic__label">
        <Heart size="1.1em" fill="currentColor" strokeWidth={0} /> Dédicaces
      </span>
      <DedicaceSpotlight dedicaces={items} variant="tv" />
    </section>
  )
})

// ---------------------------------------------------------------------
// Titre suivant
// ---------------------------------------------------------------------
const NextTrack = memo(function NextTrack() {
  const [next, setNext] = useState(null)
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await fetch(`${RADIO_API}/track/next?limit=1`)
        if (!res.ok) return
        const data = await res.json()
        const item = Array.isArray(data) ? data[0] : data
        if (!cancelled && item?.title) {
          setNext((prev) => (prev?.title === item.title ? prev : { title: item.title, artist: item.artist || '' }))
        }
      } catch { /* hors ligne */ }
    }
    load()
    const iv = setInterval(load, 30000)
    return () => { cancelled = true; clearInterval(iv) }
  }, [])

  if (!next) return null
  return (
    <div className="tvh-next">
      <span className="tvh-next__icon"><SkipForward /></span>
      <span className="tvh-next__text">
        <span className="tvh-next__label">À suivre</span>
        <span className="tvh-next__title">{next.title}</span>
        {next.artist && <span className="tvh-next__artist">{next.artist}</span>}
      </span>
    </div>
  )
})

// ---------------------------------------------------------------------
// Votes TOP / FLOP
// ---------------------------------------------------------------------
function useSongVotes(currentUser, track) {
  const songId = songIdOf(track?.title)
  const [votes, setVotes] = useState({ top: 0, flop: 0 })
  const [userVote, setUserVote] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!songId) return undefined
    const unsub = onSnapshot(doc(db, '100johnny', 'main', 'songs', songId), (snap) => {
      const d = snap.exists() ? snap.data() : {}
      setVotes((prev) => (prev.top === (d.tops || 0) && prev.flop === (d.flops || 0))
        ? prev : { top: d.tops || 0, flop: d.flops || 0 })
    }, () => {})
    return () => unsub()
  }, [songId])

  useEffect(() => {
    setUserVote(null)
    if (!currentUser || !songId) return
    getDocs(query(
      collection(db, '100johnny', 'main', 'songVotes'),
      where('userId', '==', currentUser.uid),
      where('songId', '==', songId),
      limit(1)
    )).then((snap) => setUserVote(snap.docs[0]?.data()?.type || null)).catch(() => {})
  }, [currentUser, songId])

  const vote = useCallback(async (type) => {
    if (!currentUser) { toast.error('Connectez-vous (Mon profil) pour voter'); return }
    if (!songId || busy || userVote === type) return
    setBusy(true)
    try {
      await addDoc(collection(db, '100johnny', 'main', 'songVotes'), {
        userId: currentUser.uid, songId, type,
        songTitle: track.title, songArtist: track.artist, createdAt: serverTimestamp()
      })
      await setDoc(doc(db, '100johnny', 'main', 'songs', songId), {
        title: track.title, artist: track.artist,
        [type === 'top' ? 'tops' : 'flops']: increment(1), updatedAt: serverTimestamp()
      }, { merge: true })
      setUserVote(type)
      toast.success(type === 'top' ? '👍 Merci pour votre vote' : '👎 Vote enregistré')
    } catch {
      toast.error('Le vote n\'a pas pu être enregistré')
    } finally {
      setBusy(false)
    }
  }, [currentUser, songId, busy, userVote, track])

  return { votes, userVote, busy, vote }
}

// =====================================================================
// PAGE
// =====================================================================
export default function RadioPageTV({ currentUser, globalAudio }) {
  const navigate = useNavigate()
  const {
    isPlaying, togglePlay, volume, isMuted, volumeUp, volumeDown, toggleMute, currentTrack
  } = globalAudio
  const track = currentTrack || { title: 'Radio 100% Johnny', artist: 'Johnny Hallyday', cover: null }
  const { votes, userVote, busy, vote } = useSongVotes(currentUser, track)

  // Raccourcis télécommande (touches chiffres et touche lecture)
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key
      if (k === 'MediaPlayPause' || e.keyCode === 179 || e.keyCode === 85) { e.preventDefault(); togglePlay(); return }
      if ((k === 'MediaPlay' || k === '0') && !isPlaying) { e.preventDefault(); togglePlay(); return }
      if ((k === 'MediaPause' || k === 'MediaStop' || k === '9') && isPlaying) { e.preventDefault(); togglePlay(); return }
      if (k === '1') { e.preventDefault(); vote('top'); return }
      if (k === '2') { e.preventDefault(); vote('flop') }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isPlaying, togglePlay, vote])

  const shownVolume = isMuted ? 0 : volume

  return (
    <div className="tvh tvh--promo">
      {/* BANDEAU FIXE — PARTENARIAT 500 VOIX POUR JOHNNY */}
      <div className="tvh-promo" role="note" aria-label="500 Voix pour Johnny : 20 euros de réduction">
        <span className="tvh-promo__amount">-20&nbsp;€</span>
        <span className="tvh-promo__text">
          <b>500 Voix pour Johnny</b> · Tournée 2027 · prix affichés 20&nbsp;€ déjà déduits
        </span>
        <span className="tvh-promo__url">100pour100johnny.com/500-voix-pour-johnny</span>
      </div>

      {/* EN-TÊTE */}
      <header className="tvh-top">
        <div className="tvh-brand">
          <img src="/images/logo.png" alt="" className="tvh-logo" />
          <span>Radio 100% Johnny</span>
        </div>
        <span className="tvh-live">
          {isPlaying ? (
            <span className="tvh-eq" aria-hidden="true"><i /><i /><i /></span>
          ) : (
            <span className="tvh-dot" />
          )}
          En direct
        </span>
        <Clock />
      </header>

      <Dedicaces />

      <div className="tvh-main">
        {/* LECTEUR */}
        <section className={`tvh-player ${isPlaying ? 'is-playing' : ''}`}>
          <div className="tvh-cover">
            {track.cover
              ? <img src={track.cover} alt="" />
              : <div className="tvh-cover__empty"><Music size="40%" /></div>}
          </div>

          <div className="tvh-info">
            <div className="tvh-now">En ce moment</div>
            <h1 className="tvh-title">{track.title}</h1>
            <div className="tvh-artist">{track.artist}</div>

            <button
              type="button"
              className="tvh-play"
              onClick={togglePlay}
              data-tv-initial-focus=""
              aria-label={isPlaying ? 'Mettre en pause' : 'Écouter la radio'}
            >
              {isPlaying
                ? <Pause size="1.3em" fill="currentColor" strokeWidth={0} />
                : <Play size="1.3em" fill="currentColor" strokeWidth={0} />}
              {isPlaying ? 'Pause' : 'Écouter'}
            </button>

            <div className="tvh-row">
              <button type="button" className="tvh-btn tvh-btn--sq" onClick={volumeDown} aria-label="Baisser le volume">
                <Minus size="1.2em" />
              </button>
              <div className="tvh-vol" aria-label={`Volume ${shownVolume} %`}>
                <div className="tvh-vol__bar"><i style={{ transform: `scaleX(${shownVolume / 100})` }} /></div>
                <span>{shownVolume}%</span>
              </div>
              <button type="button" className="tvh-btn tvh-btn--sq" onClick={volumeUp} aria-label="Monter le volume">
                <Plus size="1.2em" />
              </button>
              <button
                type="button"
                className={`tvh-btn tvh-btn--sq ${isMuted ? 'is-alert' : ''}`}
                onClick={toggleMute}
                aria-label={isMuted ? 'Remettre le son' : 'Couper le son'}
              >
                {isMuted ? <VolumeX size="1.2em" /> : <Volume2 size="1.2em" />}
              </button>
            </div>

            <div className="tvh-row">
              <button
                type="button"
                className={`tvh-btn ${userVote === 'top' ? 'is-on' : ''}`}
                onClick={() => vote('top')}
                disabled={busy}
              >
                <ThumbsUp size="1.1em" /> J'aime <strong>{votes.top}</strong>
              </button>
              <button
                type="button"
                className={`tvh-btn ${userVote === 'flop' ? 'is-on' : ''}`}
                onClick={() => vote('flop')}
                disabled={busy}
              >
                <ThumbsDown size="1.1em" /> Bof <strong>{votes.flop}</strong>
              </button>
            </div>

            <NextTrack />
          </div>
        </section>

        {/* MENU */}
        <nav className="tvh-menu" aria-label="Menu">
          {MENU.map(({ path, icon: Icon, label }) => (
            <button key={path} type="button" className="tvh-menu__item" onClick={() => navigate(path)}>
              <span className="tvh-menu__icon"><Icon size="1.15em" /></span>
              {label}
            </button>
          ))}

          <div className="tvh-help" aria-label="Touches de la télécommande">
            <div className="tvh-help__title"><Gamepad2 /> Télécommande</div>
            <div className="tvh-help__grid">
              <span className="tvh-key tvh-key--wide">OK</span><span>Choisir</span>
              <span className="tvh-key tvh-key--wide">◀ ▶</span><span>Se déplacer</span>
              <span className="tvh-key">0</span><span>Écouter</span>
              <span className="tvh-key">9</span><span>Pause</span>
              <span className="tvh-key">1</span><span>J'aime</span>
              <span className="tvh-key">2</span><span>Bof</span>
            </div>
          </div>
        </nav>
      </div>
    </div>
  )
}
